import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMarketActivity, getMarketActivityTrend } from '../api/metrics';
import StandaloneSection from "./shared/StandaloneSection.tsx";
import './TradeActivityMonitor.css';
import DailyFlowSummary from './market-activity/DailyFlowSummary';
import SevenDayFlowTrend from './market-activity/SevenDayFlowTrend';
import AssetBreakdown from './market-activity/AssetBreakdown';
import HourlyProfitHeatmap from './charts/HourlyProfitHeatmap';
import HourlyNetFlowHeatmap from './charts/HourlyNetFlowHeatmap';

const TradeActivityMonitor: React.FC = () => {
  const [showAssetBreakdown, setShowAssetBreakdown] = useState(false);

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  // State for selected date (defaults to today)
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());

  // Determine if selected date is today (for live indicator)
  const isToday = selectedDate === getCurrentDate();

  // Fetch market activity for selected date
  const { data: marketData, isLoading: loadingMarket, error: marketError } = useQuery({
    queryKey: ['market-activity', selectedDate],
    queryFn: () => getMarketActivity(selectedDate),
    retry: 2,
  });

  // Fetch 7-day trend centered on selected date
  const { data: trendData, isLoading: loadingTrend, error: trendError } = useQuery({
    queryKey: ['market-activity-trend', 7, selectedDate],
    queryFn: () => getMarketActivityTrend(7, selectedDate),
    retry: 2,
  });

  const hasError = marketError || trendError;

  // Handle date change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
    // Reset asset breakdown when changing dates
    setShowAssetBreakdown(false);
  };

  // Date picker and live indicator for the header
  const headerActions = (
    <div className="date-picker-container">
      <input
        type="date"
        value={selectedDate}
        onChange={handleDateChange}
        className="market-activity-date-picker"
        max={getCurrentDate()} // Prevent future dates
      />
      <div className="component-date-display">
        {isToday && <span className="today-indicator">• Live</span>}
        {!isToday && <span>Historical</span>}
      </div>
    </div>
  );

  return (
    <StandaloneSection title="TRADE ACTIVITY MONITOR" actions={headerActions}>
      <div className="market-activity-monitor">
        {hasError && (
          <div className="error-message">
            <p>Failed to load market activity data. Please try again.</p>
          </div>
        )}

        {!hasError && (
          <div className="market-activity-content">
            {/* Trading Flow Summary */}
            <section className="daily-flow-section">
              <div className="tt-section-header">
                <h3 className="tt-section-title">TODAY'S SUMMARY</h3>
                <div className="tt-section-line" />
              </div>
              <DailyFlowSummary
                data={marketData}
                isLoading={loadingMarket}
                onToggleBreakdown={() => setShowAssetBreakdown(!showAssetBreakdown)}
                showBreakdown={showAssetBreakdown}
              />
            </section>

            {/* Asset Breakdown (Collapsible) */}
            {showAssetBreakdown && marketData && (
              <section className="asset-breakdown-section">
                <AssetBreakdown assetBreakdown={marketData.assetBreakdown} />
              </section>
            )}

            {/* 7-Day Trend Chart */}
            <section className="trend-chart-section">
              <div className="tt-section-header">
                <h3 className="tt-section-title">7-DAY TRADING VOLUME TREND</h3>
                <div className="tt-section-line" />
              </div>
              <SevenDayFlowTrend data={trendData} isLoading={loadingTrend} />
            </section>

            {/* Hourly Net Flow Heatmap - New Section */}
            <section className="hourly-net-flow-section">
              <div className="tt-section-header">
                <h3 className="tt-section-title">HOURLY NET FLOW PATTERNS</h3>
                <div className="tt-section-line" />
              </div>
              <HourlyNetFlowHeatmap selectedDate={selectedDate} />
            </section>

            {/* Hourly Profit Heatmap */}
            <section className="hourly-profit-section">
              <div className="tt-section-header">
                <h3 className="tt-section-title">HOURLY PROFIT PATTERNS</h3>
                <div className="tt-section-line" />
              </div>
              <HourlyProfitHeatmap selectedDate={selectedDate} />
            </section>
          </div>
        )}
      </div>
    </StandaloneSection>
  );
};

export default TradeActivityMonitor;
